/*
 * Checklists.jsx
 * --------------
 * Shows all checklists for the current user in a responsive card grid.
 * Supports filtering by category, sorting, search, templates,
 * drag and drop reordering, and creating new checklists.
 * Works for both authenticated and guest users.
 * Consumes AuthContext and ToastContext.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { useToast } from "../App";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ChecklistCard from "../components/ChecklistCard";
import CategoryTag from "../components/CategoryTag";
import TemplateModal from "../components/TemplateModal";
import QuickAdd from "../components/QuickAdd";

// ============================================
// SORT OPTIONS
// ============================================
const SORT_OPTIONS = [
  { value: "recent",    label: "Recent" },
  { value: "dueDate",   label: "Due Date" },
  { value: "alpha",     label: "A - Z" },
  { value: "streak",    label: "Streak" },
];

const CATEGORIES = ["All", "Personal", "Work", "Home", "Shopping"];

// ============================================
// SORTABLE CARD WRAPPER
// Wraps ChecklistCard with dnd-kit sortable
// ============================================
function SortableCard({ checklist, theme }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: checklist.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto"
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ChecklistCard
        checklist={checklist}
        dragHandleListeners={listeners}
        dragHandleAttributes={attributes}
        isDragging={isDragging}
        theme={theme}
      />
    </div>
  );
}

// ============================================
// NEW CHECKLIST MODAL
// Simple inline modal for creating a checklist
// ============================================
function NewChecklistModal({ onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Personal");
  const [recurring, setRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState("weekly");
  const [targetDate, setTargetDate] = useState("");
  const [creating, setCreating] = useState(false);

  async function handleCreate() {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await onCreate({
        title: title.trim(),
        category,
        recurring,
        recurringInterval: recurring ? recurringInterval : null,
        targetDate: targetDate || null,
        items: []
      });
      onClose();
    } catch (err) {
      console.error("Failed to create checklist", err);
    } finally {
      setCreating(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleCreate();
    if (e.key === "Escape") onClose();
  }

  const INTERVALS = [
    { value: "daily",     label: "Daily" },
    { value: "weekly",    label: "Weekly" },
    { value: "monthly",   label: "Monthly" },
    { value: "monday",    label: "Every Monday" },
    { value: "wednesday", label: "Every Wednesday" },
    { value: "friday",    label: "Every Friday" },
    { value: "sunday",    label: "Every Sunday" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1200,
          animation: "fadeIn 150ms ease"
        }}
      />

      {/* Modal */}
      <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(480px, calc(100vw - 32px))",
        background: "var(--bg-surface)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        zIndex: 1201,
        overflow: "hidden",
        animation: "fadeIn 200ms ease"
      }}>

        {/* Gold top accent */}
        <div style={{
          height: "4px",
          background: "linear-gradient(90deg, var(--accent-green), var(--accent-gold))"
        }} />

        <div style={{
          padding: "var(--space-6)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-4)"
        }}>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "22px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            New Checklist
          </h2>

          {/* Title input */}
          <div>
            <label style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              display: "block",
              marginBottom: "var(--space-2)"
            }}>
              Name
            </label>
            <input
              autoFocus
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What's this checklist for?"
              maxLength={80}
              style={{
                width: "100%",
                fontFamily: "var(--font-body)",
                fontSize: "15px",
                color: "var(--text-primary)",
                background: "var(--bg-surface-2)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-3) var(--space-4)",
                outline: "none",
                transition: "border-color var(--transition-fast)",
                boxSizing: "border-box"
              }}
              onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />
          </div>

          {/* Category */}
          <div>
            <label style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              display: "block",
              marginBottom: "var(--space-2)"
            }}>
              Category
            </label>
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              {["Personal", "Work", "Home", "Shopping"].map(cat => (
                <CategoryTag
                  key={cat}
                  category={cat}
                  size="md"
                  active={category === cat}
                  onClick={() => setCategory(cat)}
                />
              ))}
            </div>
          </div>

          {/* Target date */}
          <div>
            <label style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              display: "block",
              marginBottom: "var(--space-2)"
            }}>
              Target Date (optional)
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                color: "var(--text-primary)",
                background: "var(--bg-surface-2)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-3)",
                outline: "none",
                transition: "border-color var(--transition-fast)",
                colorScheme: "dark"
              }}
              onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />
          </div>

          {/* Recurring toggle */}
          <div>
            <label style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              display: "block",
              marginBottom: "var(--space-2)"
            }}>
              Recurring
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
              <button
                onClick={() => setRecurring(prev => !prev)}
                style={{
                  width: "44px",
                  height: "24px",
                  borderRadius: "100px",
                  background: recurring ? "var(--accent-green)" : "var(--border-strong)",
                  border: "none",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background var(--transition-fast)",
                  flexShrink: 0
                }}
              >
                <div style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: "white",
                  position: "absolute",
                  top: "3px",
                  left: recurring ? "23px" : "3px",
                  transition: "left var(--transition-fast)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                }} />
              </button>
              <span style={{
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                color: recurring ? "var(--text-primary)" : "var(--text-muted)"
              }}>
                {recurring ? "Yes" : "No"}
              </span>
            </div>

            {recurring && (
              <select
                value={recurringInterval}
                onChange={e => setRecurringInterval(e.target.value)}
                style={{
                  marginTop: "var(--space-3)",
                  fontFamily: "var(--font-body)",
                  fontSize: "14px",
                  color: "var(--text-primary)",
                  background: "var(--bg-surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  outline: "none",
                  cursor: "pointer"
                }}
              >
                {INTERVALS.map(interval => (
                  <option key={interval.value} value={interval.value}>
                    {interval.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Footer buttons */}
          <div style={{
            display: "flex",
            gap: "var(--space-3)",
            justifyContent: "flex-end",
            marginTop: "var(--space-2)"
          }}>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-5)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                color: "var(--text-secondary)",
                transition: "background var(--transition-fast)"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!title.trim() || creating}
              style={{
                background: title.trim() ? "var(--accent-green)" : "var(--bg-surface-2)",
                border: "none",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-5)",
                cursor: title.trim() ? "pointer" : "not-allowed",
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                fontWeight: 600,
                color: title.trim() ? "white" : "var(--text-muted)",
                opacity: creating ? 0.7 : 1,
                transition: "opacity var(--transition-fast)"
              }}
            >
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Checklists() {
  const { user, isGuest } = useAuth();
  const { showToast } = useToast();

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [showNewModal, setShowNewModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ============================================
  // FETCH
  // ============================================
  const fetchChecklists = useCallback(async () => {
    try {
      const headers = { "Content-Type": "application/json" };
      if (isGuest) headers["x-guest-id"] = user?.id || "";
      const res = await fetch("/api/checklists", { headers });
      const data = await res.json();
      setChecklists(data);
    } catch (err) {
      console.error("Failed to fetch checklists", err);
    } finally {
      setLoading(false);
    }
  }, [user, isGuest]);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  // ============================================
  // CREATE CHECKLIST
  // ============================================
  async function handleCreate(data) {
    try {
      const headers = { "Content-Type": "application/json" };
      if (isGuest) headers["x-guest-id"] = user?.id || "";

      const res = await fetch("/api/checklists", {
        method: "POST",
        headers,
        body: JSON.stringify(data)
      });

      if (!res.ok) throw new Error("Failed to create");
      const created = await res.json();
      setChecklists(prev => [created, ...prev]);
      showToast("Checklist created");
    } catch (err) {
      console.error("Failed to create checklist", err);
    }
  }

  // ============================================
  // DRAG AND DROP REORDER
  // ============================================
  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setChecklists(prev => {
      const oldIndex = prev.findIndex(c => c.id === active.id);
      const newIndex = prev.findIndex(c => c.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  // ============================================
  // FILTER + SORT
  // ============================================
  const filtered = checklists
    .filter(c => selectedCategory === "All" || c.category === selectedCategory)
    .sort((a, b) => {
      if (sortBy === "alpha") return a.title.localeCompare(b.title);
      if (sortBy === "streak") return (b.streak || 0) - (a.streak || 0);
      if (sortBy === "dueDate") {
        const aNext = (a.items || []).filter(i => !i.checked && i.dueDate).sort((x, y) => new Date(x.dueDate) - new Date(y.dueDate))[0];
        const bNext = (b.items || []).filter(i => !i.checked && i.dueDate).sort((x, y) => new Date(x.dueDate) - new Date(y.dueDate))[0];
        if (!aNext && !bNext) return 0;
        if (!aNext) return 1;
        if (!bNext) return -1;
        return new Date(aNext.dueDate) - new Date(bNext.dueDate);
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh"
      }}>
        <p style={{ fontFamily: "var(--font-body)", color: "var(--text-muted)" }}>
          Loading checklists...
        </p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "1000px",
      margin: "0 auto",
      padding: "var(--space-6) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-5)"
    }}>

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "var(--space-3)"
      }}>
        <div>
          <h1 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 900,
            fontSize: "clamp(28px, 5vw, 40px)",
            color: "var(--text-primary)",
            margin: "0 0 var(--space-1)",
            letterSpacing: "-1px"
          }}>
            Checklists
          </h1>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "14px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            {checklists.length} list{checklists.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <button
            onClick={() => setShowTemplateModal(true)}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2) var(--space-4)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--text-secondary)",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              transition: "border-color var(--transition-fast), background var(--transition-fast)"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = "var(--accent-green)";
              e.currentTarget.style.background = "var(--accent-green-muted)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = "var(--border)";
              e.currentTarget.style.background = "none";
            }}
          >
            📋 Templates
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            style={{
              background: "var(--accent-green)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2) var(--space-4)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "14px",
              fontWeight: 600,
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              transition: "opacity var(--transition-fast)"
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            + New Checklist
          </button>
        </div>
      </div>

      {/* Filter + sort bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "var(--space-3)"
      }}>
        {/* Category filters */}
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {CATEGORIES.map(cat => (
            cat === "All" ? (
              <button
                key="All"
                onClick={() => setSelectedCategory("All")}
                style={{
                  background: selectedCategory === "All" ? "var(--accent-green)" : "none",
                  border: "1px solid " + (selectedCategory === "All" ? "var(--accent-green)" : "var(--border)"),
                  borderRadius: "100px",
                  padding: "4px 12px",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: selectedCategory === "All" ? "white" : "var(--text-muted)",
                  transition: "all var(--transition-fast)"
                }}
              >
                All
              </button>
            ) : (
              <CategoryTag
                key={cat}
                category={cat}
                size="sm"
                active={selectedCategory === cat}
                onClick={() => setSelectedCategory(cat)}
              />
            )
          ))}
        </div>

        {/* Sort dropdown */}
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            color: "var(--text-secondary)",
            background: "var(--bg-surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-2) var(--space-3)",
            cursor: "pointer",
            outline: "none"
          }}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Checklist grid */}
      {filtered.length === 0 ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-8)",
          gap: "var(--space-4)",
          textAlign: "center"
        }}>
          <span style={{ fontSize: "64px" }}>📋</span>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "24px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            {checklists.length === 0 ? "No checklists yet" : "No checklists in this category"}
          </h2>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            {checklists.length === 0
              ? "Create your first checklist or start from a template."
              : "Try selecting a different category."}
          </p>
          {checklists.length === 0 && (
            <div style={{ display: "flex", gap: "var(--space-3)" }}>
              <button
                onClick={() => setShowTemplateModal(true)}
                style={{
                  background: "none",
                  border: "1px solid var(--accent-green)",
                  borderRadius: "var(--radius-md)",
                  padding: "var(--space-3) var(--space-5)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "var(--accent-green)"
                }}
              >
                Browse Templates
              </button>
              <button
                onClick={() => setShowNewModal(true)}
                style={{
                  background: "var(--accent-green)",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  padding: "var(--space-3) var(--space-5)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "white"
                }}
              >
                Create One
              </button>
            </div>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filtered.map(c => c.id)}
            strategy={rectSortingStrategy}
          >
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "var(--space-4)"
            }}>
              {filtered.map(checklist => (
                <SortableCard
                  key={checklist.id}
                  checklist={checklist}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Modals */}
      {showNewModal && (
        <NewChecklistModal
          onClose={() => setShowNewModal(false)}
          onCreate={handleCreate}
        />
      )}
      {showTemplateModal && (
        <TemplateModal
          onClose={() => setShowTemplateModal(false)}
          onCreate={handleCreate}
        />
      )}

      {/* Mobile FAB */}
      <QuickAdd onCreate={checklist => setChecklists(prev => [checklist, ...prev])} />
    </div>
  );
}